// Add a type for the raw API response
import {FormSubmissionStatus, SUBMIT_MODE} from "../components/dynamicforms/dyanimcFormUtil.tsx";
import {
    APIComponent,
    FormComponent,
    CompSelectType,
    FormSchema,
    FormType,
    FormValues,
    ListFieldKeys,
    ValidationRuleLevel
} from "../components/dynamicforms/DynamicFormTypes.ts";
import {
    SelectableComp
} from "../components/dynamicforms/fields/configurablecompsselect/configurableCompsSelectTypes.tsx";

export interface RawMe extends Omit<Me, 'userType'> {
    userType: string;
}


export const convertUserType = (userType: string): UserType => {
    const enumKey = userType as keyof typeof UserType;
    const enumValue = UserType[enumKey];

    if (enumValue === undefined) {
        console.error(`Invalid userType received: ${userType}`);
        throw new Error('Invalid userType');
    }

    return enumValue;
};

export const convertApiUser = (raw: RawMe): Me => {
    if (!raw.securityUserId) {
        throw new Error('securityUserId is missing');
    }
    return {
        ...raw,
        userType: convertUserType(raw.userType)
    };
};

export const DEFAULT_APPRAISER: AppraiserInfo = {
    id: 0,
    officeAddressLine1: "",
    officeAddressLine2: "",
    officeCity: "",
    officeStateCode: "",
    officeZipCode: "",
    oooStartDate: "",
    oooEndDate: "",
    noTrainee: false,
    enoProvider: "",
    enoPerIncidentAmount: 0,
    enoAggregateAmount: 0,
    enoExpirationDate: "",
    isFhaCertified: false,
    isStaffAppraiser: false,
    readyForEppOnboarding: false,
    eppPartyData: 0,
    securityUser: 0,
    mobileOperatingSystemType: ""
}


export interface AppraiserInfo {
    id: number;
    officeAddressLine1: string;
    officeAddressLine2: string;
    officeCity: string;
    officeStateCode: string;
    officeZipCode: string;
    oooStartDate: string;
    oooEndDate: string;
    noTrainee: boolean;
    enoProvider: string;
    enoPerIncidentAmount: number;
    enoAggregateAmount: number;
    enoExpirationDate: string;
    isFhaCertified: boolean;
    isStaffAppraiser: boolean;
    readyForEppOnboarding: boolean;
    eppPartyData: number;
    securityUser: number;
    mobileOperatingSystemType: string;
}

export interface FullCompanyAppraiser {
    appraiserInfo: AppraiserInfo;
    securityUser: SecurityUser;
    officeAddress: string;
}

export interface AssignableAppraiser {
    id: number;
    emailAddress: string;
    firstName: string;
    lastName: string;
    officeAddress: string;
    openOrders: number;
    fhaCertified: boolean;
    licensed: boolean;
    securityUser: number
}


export interface Me {
    securityUserId: number;
    username: string;
    firstName?: string;
    lastName?: string;
    role?: string;
    authorities?: string[];
    userType: UserType;
}

export interface SecurityUser {
    id: number;
    accountActive: boolean;
    available: boolean;
    firstName: string;
    lastName: string;
    loginId: string;
    email: string;
    workPhone: string;
    cellPhone: string;
    cellPhoneCarrier: string;
}


export interface AppraiserProfileFile {
    id: number;
    uploadedBy: string;
    uploadDate: string;
    description: string;
    fileName: string;
    mimeType: string;
    fileSize: string;
    fileType: string;
}

export interface FileDownload {
    data: string;
    fileName: string;
    contentType: string;
    fileSize: number;
}

export interface PrintDownload {
    content: string;
    filename: string;
    contentType: string;
}

// Type for individual option items
export interface SelectOption {
    id?: string | number;
    value: any;
    label: string;
    extra?: string;
}

export interface SimpleSelectOption {
    value: string;
    label: string;
}

export interface SelectOptionArrayValue {
    label: string;
    value: [];
}

export interface AppraiserCoverageAreaResponse {
    appraiserCoverageAreas: AppraiserCoverageArea[];
    averageScore: number;
}

export interface License {
    id: number;
    expirationDate: string;
    ogNumber: string;
    number: string;
    stateCode: string;
    type: string;
}

export interface AppraiserCoverageArea {
    id: number | null;
    coverageStateCode: string;
    coverageCounty: string;
    zipCode: string;
    appraiserProfile: number;
    verified: boolean;
}

export enum AvailableAction {
    MARK_INSPECTED = 'MARK_INSPECTED',
    SCHEDULE_INSPECTION = 'SCHEDULE_INSPECTION',
    UPLOAD_FILES = 'UPLOAD_FILES',
    SEND_MESSAGE = "SEND_MESSAGE",
    REJECT_WITH_CONDITIONS = "REJECT_WITH_CONDITIONS",
    REJECT = "REJECT",
    ACCEPT = "ACCEPT",
    REASSIGN = "REASSIGN",
    AUTO_REPLY_YES = "AUTO_REPLY_YES",
    AUTO_REPLY_NO = "AUTO_REPLY_NO",
    FILL_OUT_FORM = "FILL_OUT_FORM",
    QC_FORM = "QC_FORM",
    VIEW_FORM_READ_ONLY = "VIEW_FORM_READ_ONLY"
}

export interface RespondQuoteFormData {
    quoteId: number;
    estimatedDeliveryDate: string;
    appraiserFee: number;
    comment: string;
}

export interface MarkAsInspectedFormData {
    orderId: number;
    inspectionActualDate: string;
    inspectionActualTime: string;
    estimatedDeliveryDate: string;
    orderStatusMessage: string;
    reasonLateDelivery: string;
}

export interface ScheduleInspectionFormData {
    orderId: number;
    inspectionActualDate: string;
    estimatedDeliveryDate: string;
    lateInspectionReason: string;
    appointmentEndTime: string;
    appointmentStartTime: string;
    appointmentScheduledWith: string;
    rescheduleReason: string
}

export enum GarageType {
    ATTACHED = "Attached",
    DETACHED = "Detached",
    CARPORT = "Carport",
    OPEN_ASSIGNED = "Open Assigned",
    COVERED_ASSIGNED = "Covered Assigned",
    BUILT_IN = "Built In",
    NONE = "None",
    OTHER = "Other",
}

export enum UserType {
    CLIENT= "CLIENT",
    APPRAISER = "APPRAISER",
    STAFF = "STAFF",
    SYSTEM = "SYSTEM",
    REGISTERED = "REGISTERED",
    SECRET = "SECRET",
    APPRAISER_ADMIN = "APPRAISER_ADMIN",
}

export enum SELECT_OPTIONS {
    STATES = 'states',
    CELL_PHONE_CARRIERS = 'cellphone-carriers',
}

export interface DashboardOrder {
    id: number;
    orderStatus: string;
    statusId: number;
    attentionIndicator: string;
    usaFileNumber: string;
    propertyAddressLine1: string;
    propertyCity: string;
    propertyStateCode: string;
    propertyZip: string;
    borrowerName: string;
    assignmentActualDate: string;
    inspectionActualDate: string;
    appraiserDueDate: string;
    appraiserUnreadMessages: boolean;
    appraiserAttentionIndicator: string;
    appraiserName: string;
}

/**
 * Paginated response from the API
 */
export interface PaginatedResponse<T> {
    items: T[];
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
}

export interface SendVerificationResponse {
    ok: boolean,
    message: string
}

/**
 * Where an emailed reset link stands (carrier PasswordResetTokenStatus); loginId is set when the link is known
 */
export interface ResetTokenStatus {
    status: 'VALID' | 'EXPIRED' | 'USED' | 'UNKNOWN',
    loginId: string | null
}

/**
 * Dashboard search request parameters matching the backend
 */
export interface DashboardSearchRequest {
    // Pagination parameters
    page: number;
    limit: number;

    // Sorting parameters
    sortField?: string;
    sortDirection?: string;

    // Search filters
    usaFileNumber?: string;
    borrowerName?: string;
    loanNumber?: string;

    // Property address filters
    propertyStreet?: string;
    propertyCity?: string;
    propertyState?: string;
    propertyZip?: string;

    // Date filters
    createDateFrom?: string;
    createDateTo?: string;

    // Status filters
    statusRefIds?: number[];
}

export interface PaymentHistorySearchRequest {

    createDateFrom?: string;
    createDateTo?: string;
}

export interface PaymentHistorySearchResponse {

    paymentDate?: string;
    usaFileNumber?: string;
    paymentAmount?: number;
    borrower?: string;
    address?: string;
}

export interface OrderContact {
    id: number;
    name: string;
    email: string;
    homePhone: string;
    cellPhone: string;
    workPhone: string;
    productOrder: number;
    type: string;
    hide: boolean;
    isPropertyContact: boolean;
}

export const DEFAULT_ORDER_DETAILS: OrderDetails = {
    id: 0,
    createDate: "",
    fileNumber: "",
    propertyAddress: "",
    county: "",
    propertyType: "",
    intendedUse: "",
    reportType1: null,
    reportType2: null,
    reportType3: null,
    reportType4: null,
    appraiserFee: 0,
    techFee: 0,
    orderedBy: "",
    orderedByAddress: "",
    notesAndSpecialInstructions: "",
    propertyContact: null,
    otherContacts: [],
    appraiserDueDate: "",
    purchasePrice: "",
    messages: [],
    files: [],
    histories: [],
    longitude: null,
    latitude: null,
    inspectionActualDate: "",
    estimatedDeliveryDate: "",
    inspectionDueDate: "",
    wasPreviouslyScheduled: false,
    assignedAppraiserSecurityUser: 0,
    loanNumber: null,
    availableActions: [],
    fhaCaseId: null,
    estimatedClosingDate: null,
    amcLicense: null,
    livingArea: null,
    lotArea: null,
    countyUse: null,
    assessedValue: null,
    yearBuilt: null,
    alerts: [],
    requiresEnv: false,
    deliveryStandard: null,
};

export interface OrderDetails {
    id: number;
    createDate: string;
    fileNumber: string;
    propertyAddress: string;
    county: string;
    propertyType: string;
    intendedUse: string;
    reportType1: ReportType | null;
    reportType2: ReportType | null;
    reportType3: ReportType | null;
    reportType4: ReportType | null;
    appraiserFee: number;
    techFee: number;
    orderedBy: string;
    orderedByAddress: string;
    notesAndSpecialInstructions: string;
    propertyContact: OrderContact | null;
    otherContacts: OrderContact[];
    appraiserDueDate: Date | string;
    purchasePrice: string;
    messages: FullMessage[];
    files: FullFile[];
    histories: FullStatus[],
    latitude: number | null;
    longitude: number | null;
    availableActions: AvailableAction[];
    inspectionActualDate: string;
    estimatedDeliveryDate: string;
    inspectionDueDate: string;
    wasPreviouslyScheduled: boolean;
    assignedAppraiserSecurityUser: number;
    loanNumber: string | null;
    fhaCaseId: string | null;
    estimatedClosingDate: string | null;
    amcLicense: string | null;
    livingArea: string | null;
    lotArea: string | null;
    countyUse: string | null;
    assessedValue: string | null;
    yearBuilt: string | null;
    alerts: string[];
    requiresEnv: boolean;
    deliveryStandard: DeliveryStandard | null;
}

export enum DeliveryStandard {
    M36= 'M36',
    M26= 'M26'
}


export interface FullMessage {
    id: number;
    productOrder: number;
    isClientMessage: boolean;
    isAppraiserMessage: boolean;
    isAcknowledgedByStaff: boolean;
    isAcknowledgedByAppraiser: boolean;
    isAcknowledgedByClient: boolean;
    isSystemMessage: boolean;
    dateCreated: string;
    messageTypeDescription: string;
    messageFromFirstName: string;
    messageFromLastName: string;
    messageText: string;
    hide: boolean;
}

export interface FullFile {
    id: number;
    fileType: number;
    fileTypeDescription: string;
    hide: boolean;
    fileSize: number;
    fileName: string;
    uploadedByFirstName: string;
    uploadedByLastName: string;
    uploadDateTime: string;
}

export interface GeneralFile {
    id: number;
    referenceId: string;
    fileName: string;
    dateUploaded: string;
    fileType: GeneralFileType;
    mimeType: string;
    metaData: GeneralFileMetaData | string;
}


export enum GeneralFileType {
    FORM_SUBMISSION = 'FORM_SUBMISSION',
}

export interface ImageSubmission {
    fieldKey: string;
    fileName: string;
    imageType: FormImageType;
    showsRepair?: boolean;
    comments?: string;
    generalFileId: number;
}

export interface FormImageRequirement {
    type: FormImageType;
    watchFieldKey?: string;
    evaluator?: string;
    hardCodedMultiplier?: number;
}


export enum FormImageType {
    NONE = 'NONE',
    FRONT = 'FRONT',
    STREET = 'STREET',
    OTHER = 'OTHER',
    SIDE = 'SIDE',
    BACK = 'BACK',
    FROM_BACK = 'FROM_BACK',
    FROM_FRONT = 'FROM_FRONT',
    ADDRESS = 'ADDRESS',
    BED = 'BED',
    BATH = 'BATH',
    KITCHEN = 'KITCHEN',
    BASEMENT = 'BASEMENT',
    ATTIC = 'ATTIC',
    EXTERIOR = 'EXTERIOR',
    UNIT = 'UNIT',
    LEFT = 'LEFT',
    RIGHT = 'RIGHT',
    ACROSS = 'ACROSS',
    AERIAL = 'AERIAL',
    BACKYARD = 'BACKYARD',
    SIGN = 'SIGN',
    FLOORPLAN = 'FLOORPLAN',
    LIVING = 'LIVING',
    DINING = 'DINING',
    INTERIOR = 'INTERIOR',
    COMPARABLE = 'COMPARABLE',
    BPO_DISCLOSURE = 'BPO_DISCLOSURE',
}


export interface GeneralFileMetaData {
    type: GeneralFileType
}

export enum FormSubmissionFileType {
    NONE = 'NONE',
}

export interface FormSubmissionGeneralFileMeta extends GeneralFileMetaData {
    type: GeneralFileType.FORM_SUBMISSION
    fileType?: FormSubmissionFileType;
    fieldId?: string;
}

export interface FullStatus {
    id: number;
    orderStatus: number;
    statusDate: string;
    orderStatusCode: string;
    orderStatusDescription: string;
    orderStatusMessage: string;
    subStatusDescription: string;
    onHoldDescription: string;
    updatedByFirstName: string;
    updatedByLastName: string;
    hide: boolean;
}


export interface ReportType {
    id: number;
    displayOrder: number;
    productDescription: string;
    threeSixProductName: string;
}

export interface FileUpload {
    file: File | null;
    fileType: string | null;
}

export interface SecureLink {
    id: number;
    viewDateTime: string;
    senderName: string;
    sentDateTime: string;
    senderEmail: string;
    recipientEmail: string;
    subject: string;
    message: string;
    productOrder: number;
    usaFileNumber: string;
    attachments: FullFile[];
}

export interface AppraiserDashQuote {
    id: number,
    propertyAddressLine1: string,
    propertyCity: string,
    propertyStateCode: string,
    propertyZipCode: string,
    dueDate: string,
    reportType: string,
    intendedUse: string,
    propertyType: string,
    clientCompany: string,
    appraiserFee: number,
}

export interface Quote {
    id: number;
    propertyAddressLine1: string;
    propertyCity: string;
    propertyStateCode: string;
    propertyZip: string;
    propertyType: number;
    intendedUse: number;
    orderedByClientCompany: number;
    reportType: number;
    reportType2: number;
    reportType3: number;
    reportType4: number;
    deliveryStandard: DeliveryStandard | null;
    appraiserFee: number;
    dueDate: Date;
    createDate: Date;
    engagers: number[];
    completed: boolean;
    createdByUser: number;
    latitude: number;
    longitude: number;
    appraisal: number;
    propertyCounty: string;
    loanId: string;
    standardAddress: string;
    contactEmail: string;
}

export interface QuoteFiles {
    id: number;
    quoteId: number;
    fileType: number;
    fileName: string;
    dateUploaded: number;
    hide: boolean;
    content: Uint8Array;
    path: string;
    mimeType: string;
    fileSize: string;
}

export interface PropertyType {
    id: number;
    description: string;
    appraisalType: string;
    displayOrder: number;
    titlePropertyType: string;
    encompassPropertyType: string;
    reports: number[];
    clients: number[];
}
export interface PropertyIntendedUse {
    id: number;
    description: string;
    displayOrder: number;
    titleTransactionType: string;
    titleLoanType: string;
    code: string;
}

export interface Client {
    id: number;
    accountActive: boolean;
    companyName: string;
    companyEmailAddress: string;
    companyWorkPhone: string;
    companyFax: string;
    mainContactName: string;
    mainContactWorkPhone: string;
    mainContactEmailAddress: string;
    billingContactName: string;
    billingContactWorkPhone: string;
    billingContactEmailAddress: string;
    officeAddressLine: string;
    officeCity: string;
    officeStateCode: string;
    officeZipCode: string;
    billingAddressSameAsOffice: boolean;
    billingAddressLine: string;
    billingCity: string;
    billingStateCode: string;
    billingZipCode: string;
    guidelines: string;
    paymentTerms: number;
    quickBooksId: string;
    allowAutoAssign: boolean;
    restrictAutoAssignToClientPanel: boolean;
    minimumRequiredAppraiserLicenseType: number;
    LinearTitleUsername: string;
    LinearTitlePassword: number;
    LinearTitleClientId: string;
    LinearTitleAvailable: boolean;
    paymentOffsetDays: number;
    canNameLender: boolean;
    clientCompanyRateTable: number;
    createdAt: Date | null;
    billingUpdatedAt: Date | null;
    officeUpdatedAt: Date | null;
    underpaymentBalance: number;
    overpaymentBalance: number;
    netBalance: number;
    deliveryInstructions: string;
    canOrderTitle: boolean;
    processCCAtStart: boolean;
    uploadToUcdp: boolean;
    sendLinkToBorrower: boolean;
    sendBorrowerRovDisclosure: boolean;
    sendReportNow: boolean;
    securePaymentLink: boolean;
    parentCompany: number;
    businessUnitNumber: string;
    fannieMaeSellerNumber: string;
    freddieMacIdNumber: string;
    orderRealView: boolean;
    mergeGuidelinesAirCert: boolean;
    ucdpAlwaysUse: boolean;
    disable_create_in_tvs: boolean;
    secure_payment_to_default_status: boolean;
    staffCompany: number;
    companyNickname: string;
    emailLogo: string;
    fhaLenderId: string;
    eadBusinessUnitNumber: string;
    standardAddress: string;
    appraisalPrecheck: boolean;
    orderEadAndUadSsr: boolean;
    defaultPaymentAmount: number;
    defaultLenderEngager: number;
    reports: number[];
    autoAssignAppraisers: number[];
    attachEncompassFolders: string[];
    blacklists: number[];
    childCompanies: number[];
    parentCompanies: number[];
    lenders: number[];
    propertyTypes: number[];
    intendedUses: number[];
    clientGroups: number[];
    salesRep: number;
    processCheckAtStart: boolean;
    sendReport3DaysLater: boolean;
    requiresElectronicConsent: boolean;
    standardOperatingProcedure: string;
    ccLo: boolean;
    requiresPaymentBeforeScheduled: boolean;
    requiresPaymentBeforeInspected: boolean;
    requiresRevisionUser: boolean;
    emailInvoiceApprover: boolean;
    paymentDeclinedStatus: boolean;
    emailFailedPayToBorrower: boolean;
    orderAvm: boolean;
    emailLenderOnRevisionRequest: boolean;
    borrowerDownloadedAppraisalFile: boolean;
    prelimReport: boolean;
    uploadUcdpOnUsda: boolean;
    lenderRequiresCertified: boolean;
    requiresCertifiedOnHighValuation: boolean;
    autoDeliverInvoices: boolean;
    propertyConditionConcern: boolean;
    allowInvites: boolean;
    overRideClientFee: boolean;
    disableAppraiserContactInfoEmail: boolean;
    disableVacancyEmail: boolean;
    trackingCode: string;
    allowAddClientUser: boolean;
    useAutoAssignPanelIfClient2: boolean;
    sendMessageWhenSecurePaymentIsSent: boolean;
    sendMessageWhenPaymentIsProcessed: boolean;
    dontSendSecurePaymentUntilAccepted: boolean;
    maxPositiveMargin: number;
    feeApprovalEmail: string;
    onlySendToFeeApprovalEmail: boolean;
    clientCompanyCardNotes: string;
    appraiserPanel: number;
    sendFaq: boolean;
    orderHomeVision: boolean;
    statusOnAppraisalUpload: number;
    allowTraineeCompleteAppraisal: string | null; // Assuming enum is represented as string in TypeScript
    dontChargeCardUntilAccepted: boolean;
    clientInvoiceApproverList: number;
    defaultFeeApprover: number;
    receiveThirdPartyDesktopUpdates: boolean;
    pendingPaymentAfterProgress: boolean;
    enableValuationExpressSchedulingEmail: boolean;
    notifyClientMissedDueDate: boolean;
    requiresAppraiserBackgroundCheck: boolean;
    requiresAppraiserCoverageAreaVerification: boolean;
    singleAppointmentMode: boolean;
    valuguardBranchId: string;
    verosAccount: any; // You may want to create a specific interface for VerosAccount
    sendInspectionConfirmationEmail: boolean;
    requiresFhaCertifiedAppraiser: boolean;
    assignmentScoringProfile: number;
    sendComplexityMessage: boolean;
    preventReassignmentByAppraisalCompany: boolean;
    includeLoanOfficerContacts: boolean;
    allowProactivePayments: boolean;
    sendAppraiserInformation: boolean;
    enableDuplicateLock: boolean;
}

export interface QuoteDetails {
    quote: Quote;
    files: QuoteFiles[];
    propertyType: PropertyType;
    intendedUse: PropertyIntendedUse;
    reportType: ReportType;
    client: Client;
}

export interface RejectQuoteRequest {
    quoteId: number;
    rejectReason: string;
}

export interface AcceptQuoteRequest {
    quoteId: number;
    appraiserFee: string;
    estimatedDeliveryDate: Date;
    comment: string;
}

export interface EppPartyInformation {
    companyName: string;
    vendorName: string;
    isLinked: boolean;
    streetAddress: string;
    state: string;
    city: string;
    zip: string;
    paymentStatus: string;
}

export interface RejectOrderRequest {
    orderId: number;
    rejectMessage: string;
}

export interface ConditionalAcceptanceRequest {
    orderId: number;
    forAppraiser?: number;
    acceptConditions: string;
    fee: string;
    dueDate: string;
    standardFee: boolean;
    complexity: boolean;
    distance: boolean;
    other: boolean
}

export interface AcceptOrderRequest {
    orderId: number;
    forAppraiser?: number;
}

export interface ReassignOrderRequest {
    orderId: number;
    appraiserToAssignTo?: number;
}

export interface UpdateStatusRequest {
    orderId: number;
    statusDescription?: string;
    message?: string
}

export interface AddFileNoteRequest {
    orderId: number;
    fileName: string;
    message: string;
}

export interface AutoReplyRequest {
    orderId: number;
}

export interface ReportFormDefinition {
    id: number;
    name: string;
    description: string;
    version: number;
    schema: string; // Note: this is String in Java, not JsonNode
    createdBy: number;
    reportType: ReportType;
    isActive: boolean;
}

export interface GradingProfilesResponse {
    profiles: GradingProfile[];
    vendorRules: string[];
    orderRules: string[];
}

export interface GradingProfile {
    gradingProfileId: number;
    eligibilityRuleType: string;
    eligibilityProfileData: EligibilityProfileData
}

export interface EligibilityProfileData {
    gatherAllContext: boolean,
    persistTrace: boolean,
    doNotGenerateInvites: boolean,
    orderEligibilityRules: string[],
    vendorEligibilityRules: string[]
}

export enum EditGradingProfileType {
    ADD_VENDOR_RULE = "ADD_VENDOR_RULE",
    DELETE_VENDOR_RULE = "DELETE_VENDOR_RULE",
    ADD_ORDER_RULE = "ADD_ORDER_RULE",
    DELETE_ORDER_RULE = "DELETE_ORDER_RULE",
    UPDATE_PERSIST_TRACE = "UPDATE_PERSIST_TRACE",
    UPDATE_GATHER_ALL_CONTEXT = "UPDATE_GATHER_ALL_CONTEXT",
    UPDATE_DO_NOT_GENERATE_INVITES = "UPDATE_DO_NOT_GENERATE_INVITES"
}

export interface EditGradingProfileRequest{
    gradingProfileId: number;
    editGradingProfileType : EditGradingProfileType;
    requestedBy?: number;
}

export interface AddOrderRuleRequest extends EditGradingProfileRequest {
    ruleToAdd: string;
}

export interface AddVendorRuleRequest extends EditGradingProfileRequest {
    ruleToAdd: string;
}

export interface DeleteVendorRuleRequest extends EditGradingProfileRequest{
    ruleToDelete: string;
}

export interface DeleteOrderRuleRequest extends EditGradingProfileRequest {
    ruleToDelete: string;
}

export interface UpdatePersistentTraceRequest extends EditGradingProfileRequest{
    state: Boolean;
}

export interface UpdateGatherAllContextRequest extends EditGradingProfileRequest{
    state: Boolean;
}

export interface UpdateDoNotGenerateInviteRequest extends EditGradingProfileRequest{
    state: Boolean;
}

export interface GeneratedFileMessage {
    filename: string;
    mimeType: string;
    content: string;
}

export interface FormSubmission {
    id: number;
    formDefinition: number,
    productOrder: number,
    submittedBy: number,
    rawSubmit?: FormValues,
    status: FormSubmissionStatus,
    submissionDate: string,
    enterStatusDate: string,
    histories: SubmissionHistory[]
}

export enum FormAction {
    QC = 'QC',
    SUBMISSION = 'SUBMISSION',
    SAVE = 'SAVE',
    QC_SUBMISSION = 'QC_SUBMISSION',
}

export interface SubmissionHistory {
    id: number,
    createDate: string,
    formAction: FormAction,
    performedBy: number,
}
export interface ReportForm {
    definition: ReportFormDefinition;
    metaData: ReportFormMetaData;
    submission: FormSubmission;
    schema: string;
}

export enum PopulationDensityCategory {
    RURAL = "Rural",
    URBAN = "Urban",
    SUBURBAN = "Suburban",
}


export const REPORT_FORM_DEFAULT_METADATA: ReportFormMetaData = {
    orderId: 922577,
    formDefinitionId: 0,
    submissionId: 0,
    streetAddress: "12024 Leavenworth Rd.",
    streetAddressLine2: "",
    city: "Kansas City",
    state: "KS",
    zip: "66109",
    county: "Wyandotte",
    longitude: "-94.8579619",
    latitude: "39.1437183",
    orderDetails: {
        client2: null,
        appraisal: {
            createDate: "2025-06-18",
            autoAssignStamp: 0,
            lastUpdateDate: "",
            assignmentDueDate: "",
            assignmentActualDate: "",
            acceptanceDueDate: "",
            acceptanceActualDate: "",
            inspectionDueDate: "",
            inspectionActualDate: "",
            clientDueDate: "",
            clientActualDate: "",
            clientDateNeeded: "",
            invoiceDueDate: "",
            invoiceActualDate: "",
            paymentReceivedDate: "",
            appointmentWindowStartTime: null,
            appointmentWindowEndTime: null,
            reviewerTimestamp: 0,
            appraiserFee1: 0,
            appraiserFee2: 0,
            appraiserFee3: 0,
            appraiserFee4: 0,
            clientFee1: 0,
            clientFee2: 0,
            clientFee3: 0,
            clientFee4: 0,
            technologyFee: 0,
            amountBilled: 0,
            clientBalance: 0,
            propertyLongitude: 0,
            propertyLatitude: 0,
            propertyContactSameAsBorrower: false,
            requiresUadCompliance: false,
            requiresFhaCompliance: false,
            invoiceClient: false,
            staffUnreadMessages: false,
            usaFileNumber: "922577",
            loanId: "",
            trackingId: "",
            fhaCaseId: "",
            appraiserFileId: "",
            clientOrderNumber: "",
            borrowerName: "",
            borrowerEmailAddress: "",
            coBorrowerName: "",
            coBorrowerEmailAddress: "",
            propertyAddressLine1: "",
            propertyAddressLine2: "",
            propertyCity: "",
            propertyStateCode: "",
            propertyZip: "",
            propertyCounty: "",
            propertyContactName: "",
            propertyContactEmailAddress: "",
            propertyContactWorkPhone: "",
            propertyContactCellPhone: "",
            propertyContactHomePhone: "",
            appointmentScheduledWith: "",
            clientCompanyNamedOnAppraisal: "",
            lenderNamedOnAppraisal: "",
            notesAndSpecialInstructions: "",
            paymentSpecialInstructions: "",
            lastFourDigitsCreditCard: "",
            checkNumber: "",
            routingNumber: "",
            checkAmount: "",
            paymentMethod: 0,
            creditCardType: 0,
            clientInvoiceApprover: 0,
            assignedAppraiser: 0,
            createdByUser: 0,
            orderStatus: 0,
            lastUpdatedByUser: 0,
            clientEngager1: 0,
            clientEngager2: 0,
            staffEngager1: 0,
            staffEngager2: 0,
            orderedByClientUser: 0,
            orderedByClientCompany: 0,
            propertyType: 0,
            propertyIntendedUse: 0,
            reportType1: 0,
            reportType2: 0,
            reportType3: 0,
            reportType4: 0,
            appraiserUnreadMessages: false,
            clientUnreadMessages: false,
            appraiserAcceptToken: "",
            childCount: 0,
            orderType: 0,
            parentOrder: 0,
            okDate: "",
            appraiserInvoiceDueDate: "",
            appraiserInvoiceActualDate: "",
            clientPaymentStatus: 0,
            appraiserPaymentStatus: 0,
            appraiserBalance: 0,
            invoiceCreateDate: "",
            flagText: "",
            clientCompanyIdedOnAppraisal: 0,
            client2Engager1: 0,
            client2Engager2: 0,
            flagLabel: 0,
            reviewer: 0,
            encompassId: "",
            markOkUntil: false,
            uldd: "",
            purchasePrice: "",
            estimatedClosingDate: "",
            balanceAlert: false,
            standardAddress: "",
            roundedPropertyLongitude: 0,
            roundedPropertyLatitude: 0,
            clearAiDate: "",
            areFilesDone: false,
            lateInspectionSuppressed: false,
            timeZone: 0,
            clientEngager3: 0,
            client2Engager3: 0,
            lastClearedStatus: 0,
            externalReview: false,
            estimatedDeliveryDate: "",
            newClientFileUpload: false,
            staffCompany: 0,
            convertedInspectionTime: "",
            eadUldd: "",
            ssrScore: 0,
            realViewScore: 0,
            autoDeliverStamp: 0,
            hasElectronicConsent: false,
            convertedAcceptanceDueDate: "",
            lockedDueDate: "",
            reportTurnAroundTime: 0,
            requiresEnv: false,
            revisionReqTime: 0,
            linkToken: "",
            proposedAppointment1Start: 0,
            proposedAppointment1End: 0,
            proposedAppointment2Start: 0,
            proposedAppointment2End: 0,
            proposedAppointment3Start: 0,
            proposedAppointment3End: 0,
            proposedAppointment4Start: 0,
            proposedAppointment4End: 0,
            propertyTimezone: "",
            scheduleInstructions: "",
            extendedTurntime: false,
            callbacks: {},
            liveDate: 0,
            valuationExpressStatus: "",
            appraiserDueDate: "",
            vacantProperty: false,
            apn: "",
            appraisalCompany: 0,
            fnmeCaseNumber: "",
            estimatedValue: 0,
            loanAmount: 0,
            autoDeliver: false,
            sendClientDueDateMessage: false,
            mortgage: 0,
            constructionProjectName: "",
            allowClone: false,
            notesToAmc: "",
            isSolar: false,
            lpaKey: "",
            duCaseNumber: "",
            appraiserMinimumFee: 0,
            lockedClientDueDate: "",
            originalClientDueDate: "",
            id: 0
        },
        requestedBy: {
            securityUser: {
                id: 0,
                accountActive: false,
                available: false,
                firstName: "",
                lastName: "",
                loginId: "",
                email: "",
                workPhone: "",
                cellPhone: "",
                cellPhoneCarrier: ""
            }
        },
        propertyType: {
            id: 0,
            description: "",
            appraisalType: "",
            displayOrder: 0,
            titlePropertyType: "",
            encompassPropertyType: "",
            reports: [],
            clients: []
        },
        intendedUse: {
            id: 0,
            description: "",
            displayOrder: 0,
            titleTransactionType: "",
            titleLoanType: "",
            code: ""
        },
        reportTypes: new Map<number, ReportType>(),
        staffCompany: undefined,
        staffCompanyLicense: undefined,
        orderedBy: {
            id: 0,
            accountActive: false,
            companyName: "",
            companyEmailAddress: "",
            companyWorkPhone: "",
            companyFax: "",
            mainContactName: "",
            mainContactWorkPhone: "",
            mainContactEmailAddress: "",
            billingContactName: "",
            billingContactWorkPhone: "",
            billingContactEmailAddress: "",
            officeAddressLine: "",
            officeCity: "",
            officeStateCode: "",
            officeZipCode: "",
            billingAddressSameAsOffice: false,
            billingAddressLine: "",
            billingCity: "",
            billingStateCode: "",
            billingZipCode: "",
            guidelines: "",
            paymentTerms: 0,
            quickBooksId: "",
            allowAutoAssign: false,
            restrictAutoAssignToClientPanel: false,
            minimumRequiredAppraiserLicenseType: 0,
            LinearTitleUsername: "",
            LinearTitlePassword: 0,
            LinearTitleClientId: "",
            LinearTitleAvailable: false,
            paymentOffsetDays: 0,
            canNameLender: false,
            clientCompanyRateTable: 0,
            createdAt: null,
            billingUpdatedAt: null,
            officeUpdatedAt: null,
            underpaymentBalance: 0,
            overpaymentBalance: 0,
            netBalance: 0,
            deliveryInstructions: "",
            canOrderTitle: false,
            processCCAtStart: false,
            uploadToUcdp: false,
            sendLinkToBorrower: false,
            sendBorrowerRovDisclosure: false,
            sendReportNow: false,
            securePaymentLink: false,
            parentCompany: 0,
            businessUnitNumber: "",
            fannieMaeSellerNumber: "",
            freddieMacIdNumber: "",
            orderRealView: false,
            mergeGuidelinesAirCert: false,
            ucdpAlwaysUse: false,
            disable_create_in_tvs: false,
            secure_payment_to_default_status: false,
            staffCompany: 0,
            companyNickname: "",
            emailLogo: "",
            fhaLenderId: "",
            eadBusinessUnitNumber: "",
            standardAddress: "",
            appraisalPrecheck: false,
            orderEadAndUadSsr: false,
            defaultPaymentAmount: 0,
            defaultLenderEngager: 0,
            reports: [],
            autoAssignAppraisers: [],
            attachEncompassFolders: [],
            blacklists: [],
            childCompanies: [],
            parentCompanies: [],
            lenders: [],
            propertyTypes: [],
            intendedUses: [],
            clientGroups: [],
            salesRep: 0,
            processCheckAtStart: false,
            sendReport3DaysLater: false,
            requiresElectronicConsent: false,
            standardOperatingProcedure: "",
            ccLo: false,
            requiresPaymentBeforeScheduled: false,
            requiresPaymentBeforeInspected: false,
            requiresRevisionUser: false,
            emailInvoiceApprover: false,
            paymentDeclinedStatus: false,
            emailFailedPayToBorrower: false,
            orderAvm: false,
            emailLenderOnRevisionRequest: false,
            borrowerDownloadedAppraisalFile: false,
            prelimReport: false,
            uploadUcdpOnUsda: false,
            lenderRequiresCertified: false,
            requiresCertifiedOnHighValuation: false,
            autoDeliverInvoices: false,
            propertyConditionConcern: false,
            allowInvites: false,
            overRideClientFee: false,
            disableAppraiserContactInfoEmail: false,
            disableVacancyEmail: false,
            trackingCode: "",
            allowAddClientUser: false,
            useAutoAssignPanelIfClient2: false,
            sendMessageWhenSecurePaymentIsSent: false,
            sendMessageWhenPaymentIsProcessed: false,
            dontSendSecurePaymentUntilAccepted: false,
            maxPositiveMargin: 0,
            feeApprovalEmail: "",
            onlySendToFeeApprovalEmail: false,
            clientCompanyCardNotes: "",
            appraiserPanel: 0,
            sendFaq: false,
            orderHomeVision: false,
            statusOnAppraisalUpload: 0,
            allowTraineeCompleteAppraisal: null,
            dontChargeCardUntilAccepted: false,
            clientInvoiceApproverList: 0,
            defaultFeeApprover: 0,
            receiveThirdPartyDesktopUpdates: false,
            pendingPaymentAfterProgress: false,
            enableValuationExpressSchedulingEmail: false,
            notifyClientMissedDueDate: false,
            requiresAppraiserBackgroundCheck: false,
            requiresAppraiserCoverageAreaVerification: false,
            singleAppointmentMode: false,
            valuguardBranchId: "",
            verosAccount: undefined,
            sendInspectionConfirmationEmail: false,
            requiresFhaCertifiedAppraiser: false,
            assignmentScoringProfile: 0,
            sendComplexityMessage: false,
            preventReassignmentByAppraisalCompany: false,
            includeLoanOfficerContacts: false,
            allowProactivePayments: false,
            sendAppraiserInformation: false,
            enableDuplicateLock: false
        },
        orderContacts: [],
        alerts: [],
        histories: [],
        files: [],
        messages:[],
    },
    generalFiles: [],
    assignedAppraiserName: "John Doe",
    assignedAppraiserLicense: "123456789",
    assignedAppraiserLicenseExpiration: "2030-12-31",
    appraiserProximity: 69,
    subjectPropertyPopulationDensity: 2,
    populationDensity: PopulationDensityCategory.SUBURBAN
}

export const POPULATION_DENSITY_MAX_DISTANCE: Record<PopulationDensityCategory, string> = {
    [PopulationDensityCategory.URBAN]: "1",
    [PopulationDensityCategory.SUBURBAN]: "5",
    [PopulationDensityCategory.RURAL]: "10",
};

export const POPULATION_DENSITY_MAP: Record<number, PopulationDensityCategory> = {
    1: PopulationDensityCategory.URBAN,
    2: PopulationDensityCategory.SUBURBAN,
    3: PopulationDensityCategory.RURAL
};

export interface ReportFormMetaData {
    orderId: number,
    formDefinitionId: number,
    submissionId: number,
    streetAddress: string,
    streetAddressLine2: string,
    city: string,
    state: string,
    zip: string,
    county: string,
    longitude: string,
    latitude: string,
    orderDetails?: FullOrderDetails,
    generalFiles: GeneralFile[],
    assignedAppraiserName: string,
    assignedAppraiserLicense: string,
    assignedAppraiserLicenseExpiration: string,
    appraiserProximity: number,
    subjectPropertyPopulationDensity: number,
    populationDensity: PopulationDensityCategory,
}


export interface PropertyDescription {
    displayName: string;
    displayValue: string;
    standardName: string;
}

export interface PropertyInformationSection {
    descriptions: PropertyDescription[];
    name: string;
}

export interface ValueRangeRequest {
    collateralAnalyticsSearchRequest?: CollateralAnalyticsSearchParams;
    orderId: number;
    address: AddressRequest;
    valuationDate?: string;
}

// comp search request
export interface CompSearchRequest {
    orderId: number;
    fullAddress?: string;
    zip: string;
    mlsListingId?: string;
}

export interface CompDetailsRequest {
    formSchema: FormSchema;
    metaData: ReportFormMetaData;
    values: FormValues;
    address: AddressRequest;
    forDataGrid?: string;
    row?: number
}

export interface CompListResponse {
    subject: Subject;
    selectableComps: SelectableComp[];
}

export interface CompListRequest {
    formSchema: FormSchema;
    metaData: ReportFormMetaData;
    values: FormValues;
    address: AddressRequest;
    collateralAnalyticsSearchRequest?: CollateralAnalyticsSearchParams;
    valuationDate?: string;
}

export interface CompDetailsResponse {
    propertyDetails: PropertyDetailsResponse;
    autoFillResponse?: AutoFillResponse;
    hiddenFields?: Record<string, HiddenFieldInfo>
}

export interface HiddenFieldInfo {

}

export interface AutoFillFieldInfo {
    message: string;
}

export interface AutoFillResponse {
    infos: Record<string, AutoFillFieldInfo>;
    values: Record<string, string>;
}



export interface Mls10DataSheetRequest {
    orderId: number,
    valuationDate?: string;
    mlsId: string;
    mlsBoardId: string;
}

export interface AddressRequest {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    zipCode: string;
}

export interface PropertyValuation {
    mlsId?: string;
    mlsBoardId?: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    proximityInMiles?: string;
    finishedBasementSquareFootage?: number;
    unfinishedBasementSquareFootage?: number;
    propertyType?: string;
    listDate?: string;
    listPrice?: number;
    originalPrice?: number;
    daysOnMarket?: number;
    calculatedDaysOnMarket?: number;
    allRoomsCount?: number;
    bedroomCount?: number;
    fullBathroomCount?: number;
    partialBathroomCount?: number;
    stories?: number;
    units?: number;
    grossLivingArea?: number;
    yearBuilt?: number;
    lotSize?: number;
    garage?: string;
    propertyAttributeType?: string;
    latitude?: number;
    longitude?: number;
    imageUrl?: string;
    allImages?: string[];
    saleStatus?: ListingStatus;
    compType?: CompSelectType;
    lastTransactionPrice?: string;
    lastTransactionDate?: string;
    lastSoldDate?: string;
    lastSoldPrice?: string;
    saleType?: string;
    estimatedPrice?: string;
    low?: string;
    high?: string;
    confidenceScore?: string;
    reoDiscount?: string;
    reoEstimated?: string;
    providerDataSource?: AvmDataSource;
    publicRemarks?: string;
    ranking?: number;
    hasFireplace?: boolean;
    [key: string]: any;
}

export enum AvmDataSource {
        DATA_QUICK = "Dataquick",
        MLS_10 = "Multiple Listing Site",
        MLS = "Multiple Listing Site",
        PUBLIC = "Public",
        UNKNOWN = "Unknown",
        MLS_10_DQ = "Multiple Listing Site",
        MSL_DQ = "Multiple Listing Site",
        MSL_20 = "Multiple Listing Site",
        MLS_10_MLS_20 = "Multiple Listing Site",
        MLS_10_MLS_20_DQ = "Multiple Listing Site",
        DYNAMIC = "Dynamic",
}

export enum ListingStatus {
    UNKNOWN = 'UNKNOWN',
    SOLD = 'SOLD',
    ACTIVE = 'ACTIVE',
    PENDING = 'PENDING',
    CONTINGENT = 'CONTINGENT',
    EXPIRED = 'EXPIRED',
    WITHDRAWN = 'WITHDRAWN',
}

export enum ConstructionType {
    MODULAR = "MODULAR",
    SITE_BUILT = "SITE_BUILT",
    MANUFACTURED = "MANUFACTURED"
}

export enum BasementType {
    BASEMENT = "BASEMENT",
    PARTIAL_BASEMENT = "PARTIAL_BASEMENT",
    CRAWLSPACE = "CRAWLSPACE",
    SLAB = "SLAB",
    OTHER = "OTHER",
    NONE = "NONE"
}

export enum StandingType {
    DETACHED = "DETACHED",
    ATTACHED = "ATTACHED"
}


export enum StyleType {
    BI_LEVEL = "BI_LEVEL",
    BUNGALOW = "BUNGALOW",
    CAPE_COD = "CAPE_COD",
    COLONIAL = "COLONIAL",
    CONTEMPORARY = "CONTEMPORARY",
    COTTAGE = "COTTAGE",
    FARM_HOUSE = "FARM_HOUSE",
    MOBILE_HOME_DOUBLE_WIDE = "MOBILE_HOME_DOUBLE_WIDE",
    MOBILE_HOME_SINGLE_WIDE = "MOBILE_HOME_SINGLE_WIDE",
    TWO_FAMILY = "TWO_FAMILY",
    RANCH = "RANCH",
    TRI_LEVEL = "TRI_LEVEL",
    VACANT_LOT = "VACANT_LOT",
    VICTORIAN = "VICTORIAN",
    OTHER_SINGLE_FAMILY = "OTHER_SINGLE_FAMILY",
    SPLIT = "SPLIT",
    THREE_FAMILY = "THREE_FAMILY",
    FOUR_FAMILY = "FOUR_FAMILY",
    GARDEN_CONDO = "GARDEN_CONDO",
    TOWNHOUSE_CONDO = "TOWNHOUSE_CONDO",
    HIGHRISE_CONDO = "HIGHRISE_CONDO"
}


export interface Subject {

    // New fields from comparable object
    comparable?: PropertyValuation;
    propertyId?: string;
    mlsPid?: string;
    rentPrice?: number;
    rentDate?: string;
    score?: number;
    allImages?: string[];
    allHistoricalTransactions?: ExtractedHistoricalTransaction[];
    filteredHistoricalTransactions?: ExtractedHistoricalTransaction[];
    extendedHistoricalTransactions?: ExtractedHistoricalTransaction[];
    isCurrentlyListed?: boolean;
    isListedInLast12Months?: boolean;
    basicPdf?: string;
    comps?: PropertyValuation[];
    listingInformation?: ExtractedListingInformation;
    publicRemarks?: string;
    style?: StyleType;
    hasBasement?: boolean;
    basement?: BasementType;
    construction?: ConstructionType;
    standingType?: StandingType;
    heating?: string;
    fuel?: string;
    cooling?: string;
    waterSource?: string;
    hasInGroundPool?: boolean;
    hasInGroundScreenedPool?: boolean;
    hasSpa?: boolean;
    garageRaw?: string;
    garageSpaces?: number;
    parking?: string;
    parkingSpaces?: string;
    garageParkingInfo?: GarageParkingInfo;
    hasDeck?: boolean;
    hasPorch?: boolean;
    hasPatio?: boolean;
    hasFence?: boolean;
    hasPool?: boolean;
    hasFireplace?: boolean;
    dataSheetLink?: string;
    basementArea?: number;
    schoolDistrictComments?: string;
    isLandLease?: boolean;
    subType?: string;
    hoaFee?: number;
    zoning?: string;
    additionalPropertyType?: string;
    sellersContribution?: number;
    ownership?: string;
    condition?: string;
    housingSupply?: string;
    percentOfDistressedSales?: number;
}

export interface PropertyDetailsResponse {
    subject: Subject;
    mls10DataSheet: MlsDataSheet;
    mls20DataSheet: MlsDataSheet;
    neighborHoodSummary?: NeighborHoodSummary | null;
}

export interface CompSearchResponse {
    comparable: PropertyValuation;
}

export interface ValueRangeResponse {
    subject: Subject;
    filteredComps: PropertyValuation[];
}

export interface Mls10DataSheetResponse {
    dataSheet: MlsDataSheet;
}

export interface MlsDataSheet {
    rawDescriptions: PropertyInformationSection[]
    // Add properties based on your MLS data sheet structure
    [key: string]: any;
    publicRemarks?: string | null
}
export interface NeighborHoodSummary {
    estimated?: number | null
    neighborHoodLow?: number | null
    neighborHoodHigh?: number | null
    confidenceScore?: number | null
}


export interface ExtractedHistoricalTransaction {
    dataSource?: string;
    listingStatus?: string; // Could be ListingStatus enum
    date?: string; // Using string for date (could be Date if preferred)
    amount?: number;
    seller?: string;
    buyer?: string;
}

export interface ExtractedListingInformation {
    status?: string;
    mostRecentSalePrice?: number;
    mostRecentSaleDate?: string; // Using string for date (could be Date if preferred)
    agentFirm?: string;
    agentName?: string;
    agentPhone?: string;
    originalListPrice?: number;
    originalListDate?: string; // Using string for date (could be Date if preferred)
    currentListDate?: string; // Using string for date (could be Date if preferred)
    currentListPrice?: number;
}

export interface GarageParkingInfo {
    attachedGarage?: boolean;
    attachedGarageSpaces?: number;
    detachedGarage?: boolean;
    detachedGarageSpaces?: number;
    builtInGarage?: boolean;
    builtInGarageSpaces?: number;
    carportGarage?: boolean;
    carportGarageSpaces?: number;
    drivewayGarage?: boolean;
    drivewayGarageSpaces?: number;
    condoGarage?: boolean;
    condoGarageSpaces?: number;
    condoCoveredGarage?: boolean;
    condoCoveredGarageSpaces?: number;
    condoOpenGarage?: boolean;
    condoOpenGarageSpaces?: number;
    noneGarage?: boolean;
}


export interface CollateralAnalyticsSearchParams {
    grossLivingArea?: string;
    lotSize?: string;
    bedroomRange?: string;
    yearBuiltRange?: string;
    bathroomRange?: string;
    propertyType?: string;
    dateRange?: string;
    searchTarget?: string;
    maxDistance?: string;
    matchSubjectsWaterfront?: boolean;
    searchType?: string;
    sales?: boolean;
    regular?: boolean;
    shortSales?: boolean;
    listings?: boolean;
    foreclosure?: boolean;
    reo?: boolean;
}

const DEFAULT_CA_SEARCH_PARAMS: CollateralAnalyticsSearchParams = {
    grossLivingArea: "",
    lotSize: "",
    bedroomRange: "",
    yearBuiltRange: "",
    bathroomRange: "",
    propertyType: "best-match",
    dateRange: "9",
    searchTarget: "25",
    maxDistance: "1",
    matchSubjectsWaterfront: false,
    searchType: "distancenozip",
    sales: true,
    listings: true,
    regular: true,
    shortSales: true,
    foreclosure: true,
    reo: true
};

const getDefaultCASearchParams = (
    maxDistance: string
): CollateralAnalyticsSearchParams => ({
    ...DEFAULT_CA_SEARCH_PARAMS,
    maxDistance: maxDistance,
});


export const coerceToCASearchParams = (
    raw: unknown,
    maxDistance: string
): CollateralAnalyticsSearchParams => {
    const base = getDefaultCASearchParams(maxDistance);

    if (!raw || typeof raw !== "object") {
        console.info("Raw search params is not an object, using defaults");
        return base;
    }

    const obj = raw as Record<string, unknown>;
    const result: CollateralAnalyticsSearchParams = { ...base };

    let typedMatches: string[] = [];
    let invalidTypes: string[] = [];
    for (const key of Object.keys(base) as (keyof CollateralAnalyticsSearchParams)[]) {
        const defaultValue = base[key];
        const candidate = obj[key];

        if (typeof candidate === typeof defaultValue) {
            typedMatches.push(`Using provided value for ${key}: ${candidate}`);
            result[key] = candidate as any;
        }
        else {
            invalidTypes.push(`Invalid type for ${key}: expected ${typeof defaultValue} but got ${typeof candidate}.`);
        }
    }
    console.info("CA Search Params Coerced: ", {
        typedMatches: typedMatches.join(" "),
        invalidTypes: invalidTypes.join(" ")
    });
    return result;
};


export interface DevRef {
    id: number;
    name: string;
    url: string;
}



export interface OriginalCompAutoFillRequest {
    fieldKey: string;
    activeFormData: ActiveFormData;
    address?: string;
    zipCode: string;
    mlsId?: string;
}

export interface ActiveFormData {
    formSchema: FormSchema;
    metaData: ReportFormMetaData;
    values: FormValues;
}

export interface OnBlurRequest {
    blurredComponent: FormComponent;
    activeFormData: ActiveFormData;
}

export interface ApiComponentRequest {
    apiComponent: APIComponent,
    activeFormData: ActiveFormData;
}

export interface AppUserDto {
    securityUser: SecurityUser;
}
export interface StaffCompany {
    id?: number;
    companyName?: string;
    companyEmailAddress?: string;
    companyWorkPhone?: string;
    companyFax?: string;
    officeAddress?: string;
    officeCity?: string;
    officeZipCode?: string;
    technologyFee?: number;
    platformUsageFee?: number;
    mainUrl?: string;
    portalUrl?: string;
    isAdmin?: boolean;
    splitFeeStates?: string[];
    engagementFeeStates?: string[];
    defaultSketchFee?: number;
    defaultPropertyDataInspectionFee?: number;
    billingEmail?: string;
    showAppraiserNicknames?: boolean
}

export interface StaffCompanyLicense {
    id: number;
    stateCode?: string;
    license?: string;
    staffCompany?: number;
    stateLongName?: string;
}

export interface AppraisalAlert {
    id: number;
    productOrder: number;
    alertType?: number;
    appraiserCanView?: boolean;
    clientCanView?: boolean;
    staffCanView?: boolean;
    hide?: boolean;
}

export interface StaffOrderDetails {
    orderDetails: FullOrderDetails;
    availableActions: AvailableAction[];
}

export interface FullOrderDetails {
    appraisal: Appraisal;
    requestedBy: AppUserDto;
    propertyType: PropertyType;
    intendedUse: PropertyIntendedUse;
    reportTypes: Map<number, ReportType>;
    staffCompany?: StaffCompany;
    staffCompanyLicense?: StaffCompanyLicense;
    orderedBy: Client;
    client2: Client | null;
    orderContacts: OrderContact[],
    alerts: AppraisalAlert[],
    histories: FullStatus[],
    files: FullFile[],
    messages: FullMessage[],
}

export interface TypedRule {
    type: FormType;
    ruleId: string
    ruleDescription: string
}


export interface Trigger {
    trigger: ListFieldKeys,
    typedRules: TypedRule[],
    dependentFields: ListFieldKeys[]
    targetFields: ListFieldKeys[]
}


export interface BackendFieldError {
    field: string;
    error: string;
    level?: ValidationRuleLevel
}

export interface SubmissionResponse {
    ok: boolean;
    fieldErrors?: BackendFieldError[];
    submission?: FormSubmission
}

export interface Appraisal {
    autoAssignStamp: number;
    lastUpdateDate: string;
    createDate: string;
    assignmentDueDate: string;
    assignmentActualDate: string;
    acceptanceDueDate: string;
    acceptanceActualDate: string;
    inspectionDueDate: string;
    inspectionActualDate: string;
    clientDueDate: string;
    clientActualDate: string;
    clientDateNeeded: string;
    invoiceDueDate: string;
    invoiceActualDate: string;
    paymentReceivedDate: string;
    appointmentWindowStartTime: null,
    appointmentWindowEndTime: null,
    reviewerTimestamp: number;
    appraiserFee1: number;
    appraiserFee2: number;
    appraiserFee3: number;
    appraiserFee4: number;
    clientFee1: number;
    clientFee2: number;
    clientFee3: number;
    clientFee4: number;
    technologyFee: number;
    amountBilled: number;
    clientBalance: number;
    propertyLongitude: number;
    propertyLatitude: number;
    propertyContactSameAsBorrower: boolean;
    requiresUadCompliance: boolean;
    requiresFhaCompliance: boolean;
    invoiceClient: boolean;
    staffUnreadMessages: boolean;
    usaFileNumber: string;
    loanId: string;
    trackingId: string;
    fhaCaseId: string;
    appraiserFileId: string;
    clientOrderNumber: string;
    borrowerName: string;
    borrowerEmailAddress: string;
    coBorrowerName: string;
    coBorrowerEmailAddress: string;
    propertyAddressLine1: string;
    propertyAddressLine2: string;
    propertyCity: string;
    propertyStateCode: string;
    propertyZip: string;
    propertyCounty: string;
    propertyContactName: string;
    propertyContactEmailAddress: string;
    propertyContactWorkPhone: string;
    propertyContactCellPhone: string;
    propertyContactHomePhone: string;
    appointmentScheduledWith: string;
    clientCompanyNamedOnAppraisal: string;
    lenderNamedOnAppraisal: string;
    notesAndSpecialInstructions: string;
    paymentSpecialInstructions: string;
    lastFourDigitsCreditCard: string;
    checkNumber: string;
    routingNumber: string;
    checkAmount: string;
    paymentMethod: number;
    creditCardType: number;
    clientInvoiceApprover: number;
    assignedAppraiser: number;
    createdByUser: number;
    orderStatus: number;
    lastUpdatedByUser: number;
    clientEngager1: number;
    clientEngager2: number;
    staffEngager1: number;
    staffEngager2: number;
    orderedByClientUser: number;
    orderedByClientCompany: number;
    propertyType: number;
    propertyIntendedUse: number;
    reportType1: number;
    reportType2: number;
    reportType3: number;
    reportType4: number;
    appraiserUnreadMessages: boolean;
    clientUnreadMessages: boolean;
    appraiserAcceptToken: string;
    childCount: number;
    orderType: number;
    parentOrder: number;
    okDate: string;
    appraiserInvoiceDueDate: string;
    appraiserInvoiceActualDate: string;
    clientPaymentStatus: number;
    appraiserPaymentStatus: number;
    appraiserBalance: number;
    invoiceCreateDate: string;
    flagText: string;
    clientCompanyIdedOnAppraisal: number;
    client2Engager1: number;
    client2Engager2: number;
    flagLabel: number;
    reviewer: number;
    encompassId: string;
    markOkUntil: boolean;
    uldd: string;
    purchasePrice: string;
    estimatedClosingDate: string;
    balanceAlert: boolean;
    standardAddress: string;
    roundedPropertyLongitude: number;
    roundedPropertyLatitude: number;
    clearAiDate: string;
    areFilesDone: boolean;
    lateInspectionSuppressed: boolean;
    timeZone: number;
    clientEngager3: number;
    client2Engager3: number;
    lastClearedStatus: number;
    externalReview: boolean;
    estimatedDeliveryDate: string;
    newClientFileUpload: boolean;
    staffCompany: number;
    convertedInspectionTime: string;
    eadUldd: string;
    ssrScore: number;
    realViewScore: number;
    autoDeliverStamp: number;
    hasElectronicConsent: boolean;
    convertedAcceptanceDueDate: string;
    lockedDueDate: string;
    reportTurnAroundTime: number;
    requiresEnv: boolean;
    revisionReqTime: number;
    linkToken: string;
    proposedAppointment1Start: number;
    proposedAppointment1End: number;
    proposedAppointment2Start: number;
    proposedAppointment2End: number;
    proposedAppointment3Start: number;
    proposedAppointment3End: number;
    proposedAppointment4Start: number;
    proposedAppointment4End: number;
    propertyTimezone: string;
    scheduleInstructions: string;
    extendedTurntime: boolean;
    callbacks: { [key: string]: string };
    liveDate: number;
    valuationExpressStatus: string;
    appraiserDueDate: string;
    vacantProperty: boolean;
    apn: string;
    appraisalCompany: number;
    fnmeCaseNumber: string;
    estimatedValue: number;
    loanAmount: number;
    autoDeliver: boolean;
    sendClientDueDateMessage: boolean;
    mortgage: number;
    constructionProjectName: string;
    allowClone: boolean;
    notesToAmc: string;
    isSolar: boolean;
    lpaKey: string;
    duCaseNumber: string;
    appraiserMinimumFee: number;
    lockedClientDueDate: string;
    originalClientDueDate: string;
    id: number;
}
export enum INFO_TYPE {
    DEFAULT = 'DEFAULT',
    OVERRIDE = 'OVERRIDE',
    CALCULATED = 'CALCULATED',
    PROVIDED = 'PROVIDED',
}

export interface FieldInfoResponse {
    type?: INFO_TYPE;
    title?: string;
    content: string;
}



export interface FormPermissions {
    submitMode: SUBMIT_MODE,
    lastNonSave?: SubmissionHistory
    userType: UserType,
    canSave: boolean,
    canEdit: boolean,
    allowTriggerApiComponent: boolean
}

export enum QCDecision {
    CANCEL = "CANCEL",
    REJECT = "REJECT",
    ACCEPT = "ACCEPT",
}
